import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react'
import { checkNeedsIndustrySelection, getSubscriptionSummary, SubscriptionSummary } from '../api/subscription'
import { useAuth } from '../hooks/useAuth'

interface SubscriptionContextValue {
  summary: SubscriptionSummary | null
  loading: boolean
  needsIndustrySelection: boolean
  refetch: () => Promise<void>
}

const SubscriptionContext = createContext<SubscriptionContextValue | undefined>(undefined)

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, loading: authLoading } = useAuth()
  const [summary, setSummary] = useState<SubscriptionSummary | null>(null)
  const [needsIndustrySelection, setNeedsIndustrySelection] = useState(false)
  const [loading, setLoading] = useState(false)

  const refetch = useCallback(async () => {
    if (!isAuthenticated) {
      setSummary(null)
      setNeedsIndustrySelection(false)
      return
    }
    setLoading(true)
    try {
      const [summaryData, needsData] = await Promise.all([
        getSubscriptionSummary().catch(() => null),
        checkNeedsIndustrySelection().catch(() => ({ needs_selection: false, reason: null })),
      ])
      setSummary(summaryData)
      setNeedsIndustrySelection(!!needsData?.needs_selection)
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated])

  useEffect(() => {
    if (authLoading) return
    refetch()
  }, [authLoading, refetch])

  return (
    <SubscriptionContext.Provider value={{ summary, loading, needsIndustrySelection, refetch }}>
      {children}
    </SubscriptionContext.Provider>
  )
}

export function useSubscription(): SubscriptionContextValue {
  const ctx = useContext(SubscriptionContext)
  if (!ctx) {
    throw new Error('useSubscription must be used inside <SubscriptionProvider>')
  }
  return ctx
}
